import { type BaseItem, type NullablePartial, type SortOrder, type Specialty } from './common.js';
import type * as DigitalCheckIn from './digitalCheckIn.js';
import type * as EhrInformation from './ehrInformation.js';
import type * as Patient from './patient.js';
import type * as Payment from './payment/payment.js';
import type * as PracticePreference from './practice/preference.js';
import type * as Practice from './practice.js';
import { type MinimalFormSet } from './previsit/formSet.js';
import type * as User from './user.js';
import type * as Workflow from './workflow.js';

export enum AppointmentStatus {
  Arrived = 'ARRIVED',
  CancellationIntended = 'CANCELLATION_INTENDED',
  Cancelled = 'CANCELLED',
  CheckedIn = 'CHECKED_IN',
  CheckedOut = 'CHECKED_OUT',
  Confirmed = 'CONFIRMED',
  NoShow = 'NO_SHOW',
  Rescheduled = 'RESCHEDULED',
  Scheduled = 'SCHEDULED',
}

export enum Source {
  Analyst = 'ANALYST',
  CalendarSync = 'CALENDAR_SYNC',
  Historical = 'HISTORICAL',
  Registration = 'REGISTRATION',
}

export enum SchedulingSource {
  Notable = 'NOTABLE',
  PatientPortal = 'PATIENT_PORTAL',
  Unknown = 'UNKNOWN',
}

export enum DigitalRegistrationStatus {
  DigitallyCheckedIn = 'DIGITALLY_CHECKED_IN',
  PreRegistrationCompleted = 'PRE_REGISTRATION_COMPLETED',
  PreRegistrationNotSent = 'PRE_REGISTRATION_NOT_SENT',
  PreRegistrationIncomplete = 'PRE_REGISTRATION_INCOMPLETE',
}

export enum CompletedSource {
  Classic = 'CLASSIC',
  ClassicFixAndLearn = 'CLASSIC_FIX_AND_LEARN',
  RealTime = 'REAL_TIME',
  RealTimeFixAndLearn = 'REAL_TIME_FIX_AND_LEARN',
}

export enum DeletedReasonSource {
  CalendarSyncNoLongerScheduled = 'CALSYNC_NO_LONGER_SCHEDULED',
  EncounterCanceledInEHR = 'ENCOUNTER_CANCELED_IN_EHR',
  ProcessingVisitEvent = 'FINGERPRINT_PROCESSING_VISIT_EVENT',
  EncounterFromProvisionalPatientExpired = 'ENCOUNTER_FROM_PROVISIONAL_PATIENT_EXPIRED',
  ManualRemoval = 'MANUAL_REMOVAL',
  Other = 'OTHER',
}

export const deletedReasonSourceMap: Record<DeletedReasonSource, string> = {
  [DeletedReasonSource.CalendarSyncNoLongerScheduled]:
    'Removed by Calendar Sync - No Longer Scheduled',
  [DeletedReasonSource.EncounterCanceledInEHR]: 'Removed after encounter canceled in EHR',
  [DeletedReasonSource.ProcessingVisitEvent]: 'Removed while processing visit event',
  [DeletedReasonSource.EncounterFromProvisionalPatientExpired]:
    'Encounter from provisional patient expired',
  [DeletedReasonSource.ManualRemoval]: 'Manually removed',
  [DeletedReasonSource.Other]: 'Removed for other reasons',
};

// repeated from ehr-export/robot-athena/src/athena/calendar.ts
type PatientStatus =
  | 'Checked Out'
  | 'Ready For Provider'
  | 'Ready For Staff'
  | 'With Provider'
  | 'With Staff';

export interface EncounterBase {
  visitId: string | null;
  ehrInformationId: string; // foreign key to (patient) ehr_information entry
  providerUserId: string;
  practiceId: string;
  insuranceProviderId: string | null;
  insuranceChanged: boolean | null;
  assignedAnalystUserId: string | null; // starts as null then becomes uuid
  assignedStructuringUserId: string | null; // starts as null then becomes uuid
  assignedTrainingSegmentsUserId: string | null; // starts as null then becomes uuid
  ehrReferenceValue: string | null; // optional value for unique encounter ID from source EHR
  ehrGuarantorId: string | null; // optional value for payer ID from source EHR
  source: Source;
  schedulingSource: SchedulingSource;
  specialty: Specialty;
  startTime: Date; // scheduled start time
  endTime: Date | null;
  kindOfVisit: string | null;
  procedureName: string | null;
  appointmentNote: string | null;
  appointmentStatus: AppointmentStatus | null;
  scenarioPostVisitId: string | null;
  patientLocation: string | null; // last known location of patient for this encounter (i.e; 'Room 1')
  medicalService: string | null;
  buildingLocation: string | null;
  ehrEncounterType: string | null;
  birthAdmissionEncounterId: string | null;
  usesFingerprintCoding: boolean; // whether an EM code has been obtained from Fingerprint
  deleted: boolean;
  deletedTimestamp: Date | null;
  deletedReason: DeletedReasonSource | null;
  // Event-related tags
  started: boolean; // started by doc on watch
  startedTimestamp: Date | null; // started by doc on watch
  endEncounterPressed: boolean; // [Whether or not] the doctor ended the encounter (pressed green checkmark on watch)
  endEncounterPressedTimestamp: Date | null; // [When] the doctor ended the encounter (pressed green checkmark on watch)
  completed: boolean; // [Whether or not] the doctor ended the encounter with the patient + all uploads complete
  completedTimestamp: Date | null; // [When] the doctor ended the encounter with the patient + all uploads complete
  completedSource: CompletedSource | null;
  completedUserAgent: string | null; // User-agent of client sending completed event
  // EHR event
  isClosed: boolean | null;
  isClosedTimestamp: Date | null;
  accidentRelated: boolean | null;
  patientDirections: string | null;
  patientInstructions: string | null;
  arrivalTime: Date | null;
  isTest: boolean | null;
}
export interface Encounter extends EncounterBase, BaseItem {}
export interface EncounterData extends Encounter {
  provider: User.User;
  practice: Practice.Practice;
  patient: Patient.PatientForPatientData;
}

export interface EncounterDataMaybeWithMinimalFormSet extends EncounterData {
  previsitFormSet?: MinimalFormSet;
}

export type CreateValues = NullablePartial<
  Pick<
    EncounterBase,
    | 'ehrInformationId'
    | 'providerUserId'
    | 'ehrReferenceValue'
    | 'startTime'
    | 'endTime'
    | 'kindOfVisit'
    | 'assignedAnalystUserId'
    | 'assignedStructuringUserId'
    | 'patientLocation'
    | 'insuranceProviderId'
  >
> &
  Partial<Pick<EncounterBase, 'specialty' | 'source' | 'visitId'>> & {
    patientStatus?: PatientStatus;
  };

export type UpdateValues = Partial<
  Pick<
    EncounterBase,
    | 'ehrInformationId'
    | 'providerUserId'
    | 'ehrReferenceValue'
    | 'startTime'
    | 'endTime'
    | 'kindOfVisit'
    | 'completed'
    | 'completedTimestamp'
    | 'assignedAnalystUserId'
    | 'assignedStructuringUserId'
    | 'patientLocation'
    | 'insuranceProviderId'
  > & {
    patientStatus?: PatientStatus;
  }
>;

export interface EncounterWithEhrInformation extends Encounter {
  practiceId: string;
  patientId: string;
  patientEhrId: string;
  ehrInformationDeleted?: string;
}

export interface EncounterWithPatient
  extends EncounterWithEhrInformation, Omit<Patient.BasePatient, 'patientEhrId'> {
  patientStatus: string | null;
  canonicalPatientStatus: PatientStatus | null;
}

export interface EncounterWithPatientAndPayment extends EncounterWithPatient {
  payment: Payment.PaymentWithVoidFlags | null;
}

export interface FindAllOptions {
  before?: Date;
  after?: Date;
  completed?: boolean;
  ehrInformationId?: string;
  organizationIds?: string[];
  orderBy?: string;
  orderByDirection?: SortOrder;
  providerUserId?: string;
  practiceId?: string;
  limit?: number;
  includeDeleted?: boolean;
  excludePostVisits?: boolean;
}

export enum SchedulingStatus {
  Confirmed = 'CONFIRMED',
  Cancelled = 'CANCELLED',
  Rescheduled = 'RESCHEDULED',
  CancellationIntended = 'CANCELLATION_INTENDED',
}

export interface FindAllViewOptionsBase {
  after?: Date;
  before?: Date;
  includeRegistration?: string;
  practiceIds?: string[];
  providerName?: string;
  patientDateOfBirth?: string;
  patientName?: string;
  patientEhrId?: string;
  workflowStatuses?: Workflow.Previsit.Status[];
  schedulingStatuses?: SchedulingStatus[];
}
export interface FindAllViewOptions extends FindAllViewOptionsBase {
  page: number;
  limit: number;
  includeDeleted?: string;
  excludePostVisits?: string;
  orderBy?: string;
  orderByDirection?: SortOrder;
}

type Practice_ = Practice.Practice;
export interface EncounterView extends Encounter {
  patientEhrId: EhrInformation.EhrInformation['value'];
  patientFirstName: Patient.Patient['firstName'];
  patientLastName: Patient.Patient['lastName'];
  patientPreferredFirstName: Patient.Patient['preferredFirstName'];
  patientPreferredLastName: Patient.Patient['preferredLastName'];
  patientDateOfBirth: Patient.Patient['dateOfBirth'];
  providerFirstName: User.User['firstName'];
  providerLastName: User.User['lastName'];
  schedulingStatus: SchedulingStatus | null;
  digitalRegistrationStatus: DigitalRegistrationStatus | null;
  workflowStatus: Workflow.Previsit.Status | null;
  practiceName: Practice_['name'];
  practiceInternalName: Practice_['internalName'];
  encounterDigitalCheckInEligibility: DigitalCheckIn.DigitalCheckInEncounter['encounterEligibility'];
  payment: Payment.Payment | null;
  frontDeskAuthCode: string | null;
  displayAuthCodeStaffApp: PracticePreference.PracticePreference['displayAuthCodeStaffApp'];
}

export type ValidationKind = 'note' | 'billing' | 'orders' | 'problems' | 'quality_measures';

export const ValidationKinds: {
  note: ValidationKind;
  billing: ValidationKind;
  orders: ValidationKind;
  problems: ValidationKind;
  quality_measures: ValidationKind;
  [kind: string]: ValidationKind;
} = {
  note: 'note',
  billing: 'billing',
  orders: 'orders',
  problems: 'problems',
  quality_measures: 'quality_measures',
};

export interface FormWithValidations {
  previsitFormSetId: string;
  previsitFormTemplateName: string;
  fieldsWithValidations: FormFieldWithValidation[];
}

export interface FormFieldWithValidation extends FormFieldWithPrimitive {
  validation: JobValidation | null;
}

export interface FormFieldWithPrimitive {
  label: string;
  value: string;
  fieldName: string;
  primitive: string;
}

export interface JobValidation {
  jobId: number;
  key: string;
  actual: any;
  expected: any;
  valid: boolean;
}

export interface IntakeForm {
  previsitFormSetId: string;
  previsitFormTemplateName: string;
  fields: FormFieldWithPrimitive[];
}

export type HasEncounterOrPrevisitFormSetId =
  | { previsitFormSetId: string }
  | { encounterId: string };

export enum Timeframe {
  Week = 'WEEK',
  Day = 'DAY',
}

export enum GetEncounterMode {
  GetOneById = 'GetOneById',
  FindAll = 'FindAll',
}
